import { ApiCall } from '../../../../../submodules/web-shared/services/http-service/types/ApiCall';
import httpService from '../../../../../submodules/web-shared/services/http-service';
import { MapZone } from '../../../../../submodules/web-shared/types/Map.types';
import { AuditParams, AuditResponse } from '../../../../../submodules/web-shared/types/AuditTrail.types';
import { FILE_EXTENSION } from '../../../../../submodules/web-shared/modules/FilesProccessing/const';
import { TableApiCall } from '../../../../../ui-lib/components/Table/types';
import { CUSTOM_LOCATION_JSON_FORMAT } from '../consts';

import {
  createIsolatedImportApiCall,
  createIsolatedReportApiCall,
  downloadTemplateFile
} from '../../../../../submodules/web-shared/modules/FilesProccessing/utils/files-api';
import locationHttpService from '../services/locationHttpService';
import { IndexLocationData } from './types/IndexLocationData';
import { IndexLocationDataFilter } from './types/IndexLocationDataFilter';
import { SingleLocationData } from './types/SingleLocationData';
import { CreateLocationParams } from './types/CreateLocationParams';
import { EditLocationParams } from './types/EditLocationParams';
import { MergeLocationExtendedParams } from './types/MergeLocationParams';
import { UpdateLocationAdditionalAttributesParams } from './types/UpdateLocationAdditionalAttributesParams';
import { LocationAdditionalAttributes } from './types/LocationAdditionalAttributes';
import { NearbyCreateLocationParams } from './types/NearbyCreateLocationParams';
import { NearbyLocation } from './types/NearbyLocation';
import { MapZonesCallParams } from './types/MapZonesCallParams';
import { LocationChanges } from './types/LocationAuditTrailParams';
import { LocationAttributesChanges } from './types/LocationAttributesAuditTrail';
import { LocationContactData } from './types/LocationContactData';
import { RouteAlternativeDeliveryLocation } from './types/RouteAlternativeDeliveryLocation';
import { DuplicateLocationsData } from './types/DuplicateLocationsData';

export const getLocations: TableApiCall<IndexLocationDataFilter, IndexLocationData> = async ({ params, abortSignal }) => {
  const { data, headers } = await locationHttpService.get('/api/v1/locations', params, abortSignal);
  return { data, headers: { total: Number(headers.total) } };
};

export const getLocation: ApiCall<{ id: number | string }, SingleLocationData> =
  async ({ params, abortSignal }) => {
    const { data } = await locationHttpService.get(`/api/v1/locations/${params.id}`, null, abortSignal);
    return data;
  };

export const deactivateMultipleLocations: ApiCall<{ location_ids: number[] }> =
  async ({ params, abortSignal }) => {
    const { data } = await locationHttpService.put('/api/v1/locations/deactivate_multiple', params, abortSignal);
    return data;
  };

export const activateMultipleLocations: ApiCall<{ location_ids: number[] }> =
  async ({ params, abortSignal }) => {
    const { data } = await locationHttpService.put('/api/v1/locations/activate_multiple', params, abortSignal);
    return data;
  };

export const generateIsolatedImportLocationsFile = createIsolatedImportApiCall('/api/v1/locations/import_data_url', undefined, locationHttpService);

export const deactivateLocation: ApiCall<{ id: number }> = async ({ params, abortSignal }) => {
  const { data } = await locationHttpService.put(`/api/v1/locations/${params.id}/deactivate`, null, abortSignal);
  return data;
};

export const activateLocation: ApiCall<{ id: number }> = async ({ params, abortSignal }) => {
  const { data } = await locationHttpService.put(`/api/v1/locations/${params.id}/activate`, null, abortSignal);
  return data;
};

export const downloadLocationsTemplate = () =>
  downloadTemplateFile('/api/v1/locations/create_multiple_template', 'locations.xls', undefined, locationHttpService);

export const createLocation: ApiCall<
  CreateLocationParams,
  SingleLocationData
> = async ({ params, abortSignal }) => {
  const { data } = await locationHttpService.post('/api/v1/locations', params, abortSignal);
  return data;
};

export const updateLocation: ApiCall<EditLocationParams, SingleLocationData> = async ({ params, abortSignal }) => {
  const { data } = await locationHttpService.put(`/api/v1/locations/${params.id}`, params, abortSignal);
  return data;
};

export const updateAdditionalAttributes: ApiCall<UpdateLocationAdditionalAttributesParams, LocationAdditionalAttributes> = async ({ params, abortSignal }) => {
  const { data } = await httpService.put('/api/v3/device_management/sites/upsert', params, abortSignal);
  return data;
};

export const getAdditionalAttributes: ApiCall<{ location_id: number | string }, LocationAdditionalAttributes> = async ({ params, abortSignal }) => {
  const { data } = await httpService.get(`/api/v3/device_management/sites/${params.location_id}`, null, abortSignal);
  return data;
};

export const getNearbyLocations: ApiCall<
  NearbyCreateLocationParams,
  NearbyLocation[]
> = async ({ params, abortSignal }) => {
  const { data } = await locationHttpService.get('/api/v1/locations/nearby', params, abortSignal);
  return data;
};

export const getMapZones: ApiCall<MapZonesCallParams, MapZone[]> = async ({ params, abortSignal }) => {
  const { data } = await locationHttpService.get('/api/v1/locations/map_list', params, abortSignal);
  return data;
};

export const generateIsolatedExportLocationsFile = createIsolatedReportApiCall('/api/v1/locations/report_data_url', FILE_EXTENSION.CSV, locationHttpService);

export const generateIsolatedExportLocationsJSONFile = createIsolatedReportApiCall('/api/v1/locations/report_geo_json_url', CUSTOM_LOCATION_JSON_FORMAT, locationHttpService);

export const fetchLocationAudit = (locationId: number) => {
  const fetch: ApiCall<AuditParams, AuditResponse<LocationChanges>> = async ({ params, abortSignal }) => {
    const { data, headers } = await locationHttpService.get(`/api/v1/locations/${locationId}/audit`, params, abortSignal);
    return { data, headers: { total: Number(headers.total) } };
  };
  return fetch;
};

export const fetchLocationAttributesAudit = (locationId: number) => {
  const fetch: ApiCall<AuditParams, AuditResponse<LocationAttributesChanges>> = async ({ params, abortSignal }) => {
    const { data, headers } = await httpService.get(`/api/v3/device_management/sites/${locationId}/audit`, params, abortSignal);
    return { data, headers: { total: Number(headers.total) } };
  };
  return fetch;
};

export const mergeLocationsExtended: ApiCall<MergeLocationExtendedParams, SingleLocationData> = async ({ params, abortSignal }) => {
  const { data } = await locationHttpService.put('/api/v1/locations/merge_locations_extended', params, abortSignal);
  return data;
};

export const getLocationContacts: TableApiCall<{ location_id: number }, LocationContactData> = async ({ params, abortSignal }) => {
  const { data, headers } = await httpService.get(
    '/api/v3/account/contact_users/location_contacts',
    params,
    abortSignal
  );
  return { data, headers: { total: Number(headers.total) } };
};

export const getLocationAlternateDeliveries: ApiCall<{ id: number }, RouteAlternativeDeliveryLocation[]> = async ({ params, abortSignal }) => {
  const { data } = await locationHttpService.get(`/api/v1/locations/${params.id}/alternate_deliveries`, null, abortSignal);
  return data;
};

export const duplicateLocationsToPortals: ApiCall<{ location_ids: number[]; portal_ids: number[] }, DuplicateLocationsData> = async ({ params, abortSignal }) => {
  const { data } = await locationHttpService.post('/api/v1/locations/clone_zones', params, abortSignal);
  return data;
};